import type { ReactNode } from "react";

import { BELEG_DOC_TYPE_LABEL, type BelegDocType } from "../api/belege";
import BelegExternalNumber from "./BelegExternalNumber";
import BelegNeighbours from "./BelegNeighbours";
import CostCenterValue from "./CostCenterValue";
import DocumentPdfActions from "./DocumentPdfActions";
import type { ProjectSummary } from "../api/projects";
import { isDemoMode } from "../lib/env";
import { formatDateShort } from "../lib/format";

// The same head for every document kind (#546): what it is, who sent it, which
// cost center it belongs to, its own accounting number and where it stands.
// Only the actions below differ per kind.

export type HeaderCostCenter = { projectKey: string | null; deliveryAddressDisplay?: string | null };

/** Cost center of the whole document: the one most of its positions carry. */
export function dominantCostCenter(positions: readonly HeaderCostCenter[]): {
  costCenter: HeaderCostCenter | null;
  otherCount: number;
} {
  const byKey = new Map<string, { entry: HeaderCostCenter; count: number }>();
  for (const position of positions) {
    if (!position.projectKey) continue;
    const seen = byKey.get(position.projectKey);
    if (seen) seen.count += 1;
    else byKey.set(position.projectKey, { entry: position, count: 1 });
  }
  const ranked = [...byKey.values()].sort((a, b) => b.count - a.count);
  return { costCenter: ranked[0]?.entry ?? null, otherCount: Math.max(ranked.length - 1, 0) };
}

export default function BelegDetailHeader({
  docType,
  docId,
  docNumber,
  docDate,
  supplier,
  buyer,
  pdfAvailable,
  pdfDocId,
  positions = [],
  projectByKey,
  status,
  actions,
}: {
  docType: BelegDocType;
  docId: string;
  docNumber: string | null;
  docDate: string | null;
  supplier: string | null;
  buyer?: string | null;
  pdfAvailable: boolean;
  pdfDocId?: string | null;
  positions?: readonly HeaderCostCenter[];
  projectByKey?: ReadonlyMap<string, ProjectSummary>;
  /** Review state of the document, rendered by the page that knows it. */
  status?: ReactNode;
  /** Extra head elements of one kind, e.g. a link back to its order. */
  actions?: ReactNode;
}) {
  const { costCenter, otherCount } = dominantCostCenter(positions);
  // The ER number is the customer's own accounting number and exists for
  // invoices and credits only (#529).
  const showExternalNumber = !isDemoMode() && (docType === "rechnung" || docType === "gutschrift");

  return (
    <header className="mt-4 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        {/* Kind and number are the heading; nothing repeats them below. */}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">
            {BELEG_DOC_TYPE_LABEL[docType]} <span className="font-mono">{docNumber ?? docId}</span>
          </h1>
          {status}
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
          <span>vom {formatDateShort(docDate)}</span>
          {supplier ? <span>· {supplier}</span> : null}
          {buyer ? <span>· {buyer}</span> : null}
        </p>
        {positions.length > 0 ? (
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="text-slate-600">Kostenstelle</span>
            {costCenter ? (
              <CostCenterValue
                projectKey={costCenter.projectKey}
                deliveryAddressDisplay={costCenter.deliveryAddressDisplay}
                projectByKey={projectByKey}
                compact
              />
            ) : (
              <span className="text-slate-500">Nicht zugeordnet</span>
            )}
            {otherCount > 0 ? (
              <span className="text-xs text-slate-500">
                und {otherCount} weitere in den Positionen
              </span>
            ) : null}
          </p>
        ) : null}
        {showExternalNumber ? <BelegExternalNumber belegId={docId} /> : null}
      </div>
      <div className="flex flex-col items-end gap-2">
        <BelegNeighbours belegId={docId} />
        {pdfAvailable ? (
          <DocumentPdfActions
            docId={pdfDocId ?? docId}
            docNumber={docNumber ?? docId}
            linePos={null}
            className="mt-1 text-sm"
          />
        ) : null}
        {actions}
      </div>
    </header>
  );
}
